/**
 * CustomProductsAutocomplete - Service/Product selector
 *
 * Enhanced with category, service line, and points info
 * for consistent selection experience matching Roadmap Builder.
 */

import { Autocomplete, Box, Chip, CircularProgress, Popper, TextField, Typography, alpha, useTheme } from '@mui/material'
import { styled, SxProps } from '@mui/material/styles'
import React from 'react'
import { Product } from 'types'
import { productAutocompleteSx } from 'styles/utilities/view-agreement'

const CustomPopper = styled(Popper)({
  width: '100% !important',
  minWidth: '280px',
  maxWidth: '520px',
})

interface CustomProductsProps {
  products: Product[]
  value: Product
  onChange: (newValue: any) => void
  label: string
  error: boolean
  isLoading: boolean
  disabled?: boolean
  sx?: SxProps
  getOptionDisabled?: (option: any) => boolean
  getOptionArchived?: (option: any) => boolean
}

const CustomProductsAutocomplete: React.FC<CustomProductsProps> = ({
  products,
  value,
  onChange,
  label,
  error,
  isLoading,
  getOptionDisabled,
  getOptionArchived,
  disabled,
  sx,
}) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  const handleOnChange = (_event: any, newValue: any) => {
    onChange(newValue)
  }

  // Get centralized styles and merge with any custom sx
  const baseStyles = productAutocompleteSx(theme)
  const mergedStyles = sx ? { ...baseStyles, ...(sx as object) } : baseStyles

  // Category color mapping
  const getCategoryColor = (category?: string) => {
    if (!category) return alpha(theme.palette.text.primary, 0.06)
    const lower = category.toLowerCase()
    if (lower.includes('seo')) return alpha(theme.palette.text.complete, 0.12)
    if (lower.includes('ppc') || lower.includes('paid')) return alpha(theme.palette.text.link, 0.12)
    if (lower.includes('social')) return alpha(theme.palette.text.ai_accent, 0.12)
    if (lower.includes('design') || lower.includes('creative')) return alpha(theme.palette.text.highlight, 0.12)
    if (lower.includes('dev') || lower.includes('web')) return alpha(theme.palette.text.error, 0.12)
    if (lower.includes('content') || lower.includes('copy')) return 'rgba(14,165,233,0.12)'
    if (lower.includes('email')) return 'rgba(236,72,153,0.12)'
    if (lower.includes('strategy') || lower.includes('consult')) return 'rgba(99,102,241,0.12)'
    return alpha(theme.palette.text.primary, 0.04)
  }

  const getCategoryTextColor = (category?: string) => {
    if (!category) return theme.palette.text.secondary
    const lower = category.toLowerCase()
    if (lower.includes('seo')) return theme.palette.text.complete
    if (lower.includes('ppc') || lower.includes('paid')) return theme.palette.text.link
    if (lower.includes('social')) return theme.palette.text.ai_accent
    if (lower.includes('design') || lower.includes('creative')) return theme.palette.text.highlight
    if (lower.includes('dev') || lower.includes('web')) return theme.palette.text.error
    if (lower.includes('content') || lower.includes('copy')) return '#0EA5E9'
    if (lower.includes('email')) return '#EC4899'
    if (lower.includes('strategy') || lower.includes('consult')) return '#6366F1'
    return theme.palette.text.secondary
  }

  return (
    <Box sx={{ width: '100%' }}>
      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <Autocomplete
          PopperComponent={CustomPopper}
          value={value || null}
          options={products}
          getOptionLabel={option => option.title || ''}
          onChange={handleOnChange}
          groupBy={option => option.category || 'Other'}
          renderGroup={params => (
            <li key={params.key}>
              <Box
                sx={{
                  position: 'sticky',
                  top: -8,
                  px: 1.5,
                  py: 0.5,
                  bgcolor: alpha(theme.palette.background.paper, 0.97),
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  zIndex: 1,
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: getCategoryTextColor(params.group),
                  }}
                >
                  {params.group}
                </Typography>
              </Box>
              <ul style={{ padding: 0 }}>{params.children}</ul>
            </li>
          )}
          renderOption={(props, option) => {
            const isDisabledOpt = !!getOptionDisabled?.(option) || false
            const isArchived = !!getOptionArchived?.(option) || false
            const hasPoints = option.points && Number(option.points) > 0
            const hasServiceLine = !!option.service_line

            return (
              <li
                {...props}
                key={option.products_uuid || option.id}
                aria-disabled={isDisabledOpt || undefined}
                style={{
                  ...(props as any).style,
                  opacity: isDisabledOpt ? 0.45 : 1,
                  pointerEvents: isDisabledOpt ? 'none' : 'auto',
                  padding: '6px 12px',
                }}
              >
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {/* Row 1: Title + status */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {option.title}
                    </Typography>
                    {isDisabledOpt && (
                      <Chip
                        label="Selected"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          fontWeight: 600,
                          bgcolor: alpha(theme.palette.text.link, 0.1),
                          color: theme.palette.text.link,
                        }}
                      />
                    )}
                    {isArchived && (
                      <Chip
                        label="Archived"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          fontWeight: 600,
                          bgcolor: alpha(theme.palette.text.error, 0.1),
                          color: theme.palette.text.error,
                        }}
                      />
                    )}
                  </Box>
                  {/* Row 2: Metadata chips */}
                  {(hasServiceLine || hasPoints || option.subcategory) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      {option.subcategory && (
                        <Chip
                          label={option.subcategory}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: 9.5,
                            fontWeight: 600,
                            bgcolor: getCategoryColor(option.category),
                            color: getCategoryTextColor(option.category),
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      )}
                      {hasServiceLine && (
                        <Typography
                          sx={{
                            fontSize: 10,
                            color: 'text.secondary',
                            fontWeight: 500,
                          }}
                        >
                          {option.service_line}
                        </Typography>
                      )}
                      {hasPoints && (
                        <Typography
                          sx={{
                            fontSize: 10,
                            color: theme.palette.text.highlight,
                            fontWeight: 600,
                            fontVariantNumeric: 'tabular-nums',
                            ml: 'auto',
                          }}
                        >
                          {option.points} pts
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </li>
            )
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              error={error}
            />
          )}
          isOptionEqualToValue={(option, val) => option.uuid === (val ? val.uuid : undefined)}
          fullWidth
          disabled={disabled}
          sx={mergedStyles}
        />
      )}
    </Box>
  )
}

export default CustomProductsAutocomplete
